import { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Minus, Pencil, Plus } from 'lucide-react'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { useFlavorManager } from '@cannect/hooks/useFlavorManager'
import { useGetProductTiers } from '@cannect/services/resources/progressive-discounts'

const GREEN = '#748351'

interface FlavorQuantitySelectorProps {
  /** product_id do item de carrinho da linha atual (sabor hoje selecionado nessa linha). */
  productId: number
  /** main_product_id da família de sabores — usado pra buscar todas as variações. Fallback: productId. */
  mainProductId?: number
  /** Nome do sabor atual — exibido no botão disparador. Fallback: nome vindo da própria lista buscada. */
  currentFlavorLabel?: string
  /**
   * true → popover via portal (position:fixed), escapa overflow de ancestral (drawer).
   * false (padrão) → position:absolute, para telas amplas (carrinho-wa, /carrinho).
   */
  overlay?: boolean
  className?: string
}

interface FlavorItem {
  id: number
  flavor: string
  balance?: number
  status?: string
}

interface FlavorRowProps {
  flavor: FlavorItem
  qty: number
  onChange: (flavorId: number, newQty: number) => void
}

/** Linha de um sabor dentro do popover: nome, destaque de desconto progressivo, estoque e stepper de qtd. */
function FlavorRow({ flavor, qty, onChange }: FlavorRowProps) {
  const { data: tiersData } = useGetProductTiers(flavor.id)
  const maxTierPct = (tiersData?.tiers ?? []).reduce((max, t) => Math.max(max, Number(t.discount_percent) || 0), 0)
  const showProgressiveTag = !!tiersData?.has_progressive_discount && maxTierPct > 0
  const outOfStock = (flavor.balance ?? 0) <= 0

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 6px' }}>
      <div style={{ display: 'flex', minWidth: 0, flexDirection: 'column', gap: 3 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#3f3f3f',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
          {flavor.flavor}
        </span>
        {(showProgressiveTag || outOfStock) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {showProgressiveTag && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#fff',
                  background: GREEN,
                  borderRadius: 999,
                  padding: '2px 7px',
                  whiteSpace: 'nowrap'
                }}>
                até {maxTierPct}% OFF
              </span>
            )}
            {outOfStock && <span style={{ fontSize: 10.5, color: '#b3856b' }}>possível maior tempo de entrega</span>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
        <button
          type="button"
          disabled={qty <= 0}
          onClick={() => onChange(flavor.id, Math.max(0, qty - 1))}
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: '1.5px solid #cfcfcf',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: qty <= 0 ? 'default' : 'pointer',
            opacity: qty <= 0 ? 0.4 : 1
          }}>
          <Minus size={12} color={GREEN} />
        </button>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#3f3f3f', minWidth: 16, textAlign: 'center' }}>
          {qty}
        </span>
        <button
          type="button"
          onClick={() => onChange(flavor.id, qty + 1)}
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: `1.5px solid ${GREEN}`,
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}>
          <Plus size={12} color={GREEN} />
        </button>
      </div>
    </div>
  )
}

/**
 * Seletor unificado de sabor + quantidade — reutilizado no cartSheet (drawer), /carrinho v2 e
 * /carrinho-wa. Lista todos os sabores da família (via useFlavorManager, mesma fonte do pop-up
 * legado SelectFlavorModal: GET /get_product_flavors/:id + /product/:id) com um stepper de
 * quantidade por sabor. Aumentar um sabor diferente do atual ADICIONA sem alterar os demais
 * (AddToCartByFlavor seta quantidade absoluta por product_id sem tocar em outros itens); zerar um
 * sabor remove (removeCart) — o "trocar" sai naturalmente de zerar o atual + somar no novo.
 */
export function FlavorQuantitySelector({
  productId,
  mainProductId,
  currentFlavorLabel,
  overlay = false,
  className
}: FlavorQuantitySelectorProps) {
  const { lastOrderCreated, AddToCartByFlavor, removeCart } = usePrepareCart()
  const { preloadProductFlavors, getProductFlavors, loadingFlavors } = useFlavorManager()
  const [open, setOpen] = useState(false)
  // Overrides otimistos: só guarda os sabores que o usuário mexeu e ainda não foram confirmados
  // pelo carrinho real. Pra qualquer outro sabor, o qty exibido vem direto de cartQtyByProductId.
  const [pendingQty, setPendingQty] = useState<Record<number, number>>({})
  const pendingQtyRef = useRef<Record<number, number>>({})
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  const familyId = mainProductId || productId

  useEffect(() => {
    preloadProductFlavors(productId, mainProductId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId])

  const flavors = (getProductFlavors(familyId) as FlavorItem[]).filter((f) => f.status === 'A')
  const isLoadingFlavors = !!loadingFlavors[familyId]

  const cartQtyByProductId = useMemo(() => {
    const map = new Map<number, number>()
    ;(lastOrderCreated?.items || []).forEach((ci: any) => {
      const pid = Number(ci?.product_id || ci?.product?.id || ci?.id)
      map.set(pid, Number(ci?.quantity) || 0)
    })
    return map
  }, [lastOrderCreated?.items])

  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [portalPos, setPortalPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    if (!overlay) return
    if (open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      const idealLeft = r.left + r.width / 2 - 150
      const left = Math.max(8, Math.min(idealLeft, window.innerWidth - 308))
      setPortalPos({ top: r.bottom + 8, left })
    } else {
      setPortalPos(null)
    }
  }, [overlay, open])

  // flushRef é reatribuído a cada render pra sempre fechar sobre o cartQtyByProductId/mutations mais
  // recentes — necessário porque o setTimeout do debounce roda fora do ciclo normal de render.
  const flushRef = useRef<() => void>(() => {})
  flushRef.current = async () => {
    const snapshot = pendingQtyRef.current
    const entries = Object.entries(snapshot).map(([id, qty]) => ({ flavorId: Number(id), qty }))
    const noOps = entries.filter((e) => e.qty === (cartQtyByProductId.get(e.flavorId) ?? 0))
    const changed = entries.filter((e) => e.qty !== (cartQtyByProductId.get(e.flavorId) ?? 0))

    // Quem voltou pro valor que já está no carrinho (ex.: clique - depois +) não precisa de request —
    // limpa o override na hora, senão ficaria preso em pendingQty pra sempre.
    if (noOps.length && mountedRef.current) {
      setPendingQty((prev) => {
        const next = { ...prev }
        noOps.forEach(({ flavorId }) => delete next[flavorId])
        pendingQtyRef.current = next
        return next
      })
    }

    if (!changed.length) return

    const toAdd = changed.filter((c) => c.qty > 0).map((c) => ({ product_id: c.flavorId, quantity: c.qty }))
    const toRemove = changed.filter((c) => c.qty <= 0).map((c) => c.flavorId)

    try {
      await Promise.all([
        toAdd.length ? AddToCartByFlavor(toAdd) : Promise.resolve(),
        ...toRemove.map((id) => removeCart(id))
      ])
    } finally {
      // Reconcilia: só limpa o override de quem ainda está com o MESMO valor que acabou de ser
      // enviado — se o usuário mudou de novo durante a request, mantém o valor mais novo (já tem
      // um novo ciclo de debounce agendado por esse clique, que vai persistir depois).
      if (mountedRef.current) {
        setPendingQty((prev) => {
          const next = { ...prev }
          changed.forEach(({ flavorId, qty }) => {
            if (next[flavorId] === qty) delete next[flavorId]
          })
          pendingQtyRef.current = next
          return next
        })
      }
    }
  }

  const scheduleFlush = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      flushRef.current()
    }, 333)
  }

  // Fecha (e persiste imediatamente qualquer mudança pendente, sem esperar o debounce de 333ms)
  const closePopover = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
      flushRef.current()
    }
    setOpen(false)
  }

  // Cleanup: limpa o timer e persiste qualquer mudança pendente ao desmontar (drawer fechado, item
  // removido do grupo etc.) — sem isso, um clique feito bem antes do desmontar se perderia.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
        flushRef.current()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fecha ao rolar a página, mas ignora scroll originado dentro do próprio popover (lista longa de sabores)
  useEffect(() => {
    if (!overlay || !open) return undefined
    const close = (e: Event) => {
      if (popoverRef.current && e.target instanceof Node && popoverRef.current.contains(e.target)) return
      closePopover()
    }
    window.addEventListener('scroll', close, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', close, { capture: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, open])

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return undefined
    const handleDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-flavor-qty-dd]')) closePopover()
    }
    document.addEventListener('mousedown', handleDown)
    return () => document.removeEventListener('mousedown', handleDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Otimista: atualiza a qtd exibida na hora, sem travar — a persistência real é debounced (333ms)
  // e batcheada (uma única AddToCartByFlavor pros sabores com qty>0 nesse ciclo).
  const handleChange = (flavorId: number, newQty: number) => {
    const next = { ...pendingQtyRef.current, [flavorId]: newQty }
    pendingQtyRef.current = next
    setPendingQty(next)
    scheduleFlush()
  }

  const displayQty = (flavorId: number) => pendingQty[flavorId] ?? cartQtyByProductId.get(flavorId) ?? 0

  const hasFlavorData = !!currentFlavorLabel || flavors.length > 0
  const displayLabel = currentFlavorLabel || flavors.find((f) => f.id === productId)?.flavor

  // Buscando sabores pela primeira vez (sem cache ainda) — mostra loading em vez de nada/vazio
  if (isLoadingFlavors && flavors.length === 0) {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg className="animate-spin" width={12} height={12} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke={GREEN} strokeWidth="2.5" opacity={0.25} />
          <path d="M12 3a9 9 0 0 1 9 9" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        Sabor: carregando...
      </span>
    )
  }

  if (!hasFlavorData) {
    return null
  }

  // Sem 2+ sabores ativos: nada pra trocar — mantém só o texto (igual ao "Sabor: X" estático de hoje)
  if (flavors.length <= 1) {
    return <span className={className}>Sabor: {displayLabel}</span>
  }

  const popoverBaseStyle: CSSProperties = {
    width: 280,
    maxHeight: 300,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e7e6da',
    borderRadius: 14,
    boxShadow: '0 24px 50px -18px rgba(63,63,50,.45)',
    padding: 10
  }

  const popoverInner = (
    <>
      <h4 style={{ margin: '2px 4px 8px', fontSize: 12.5, fontWeight: 700, color: '#3f3f3f' }}>Escolha os sabores</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {flavors.map((f) => (
          <FlavorRow key={f.id} flavor={f} qty={displayQty(f.id)} onChange={handleChange} />
        ))}
      </div>
    </>
  )

  return (
    <div className={className} style={{ position: 'relative', display: 'inline-flex' }} data-flavor-qty-dd>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? closePopover() : setOpen(true))}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          border: `1px solid ${open ? GREEN : '#e2e2da'}`,
          background: '#fafaf7',
          cursor: 'pointer'
        }}>
        <span style={{ fontSize: 11.5, color: '#6b6b64' }}>Sabor:</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: '#3f3f3f' }}>{displayLabel}</span>
        <Pencil size={11} color="#8a8a8a" />
      </button>

      {!overlay && open && (
        <div style={{ ...popoverBaseStyle, position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 60 }}>
          {popoverInner}
        </div>
      )}

      {overlay &&
        open &&
        portalPos &&
        createPortal(
          <div
            ref={popoverRef}
            data-flavor-qty-dd
            style={{ ...popoverBaseStyle, position: 'fixed', top: portalPos.top, left: portalPos.left, zIndex: 9999 }}>
            {popoverInner}
          </div>,
          document.body
        )}
    </div>
  )
}
